/**
 * Project Billing Workbook module
 *
 * List / Upload page for construction contractor billing workbooks in the
 * "Faiz Sir IT Park" stacked-section format. Users upload their Excel, we
 * parse every project × block × scope × milestone × receipt, and let them
 * commit — after which each receipt cross-links into the Bank Ledger drill-down.
 *
 * Route: /project-billing
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { toast } from "sonner";
import { fmtINR } from "../lib/format";
import {
    FileSpreadsheet, Upload, Sparkles, CheckCircle2, Trash2, FolderKanban,
    Layers, ChevronRight, AlertTriangle, ArrowRight
} from "lucide-react";

export default function ProjectBilling() {
    const [workbooks, setWorkbooks] = useState([]);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [linkedClient, setLinkedClient] = useState("");
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [linkedProject, setLinkedProject] = useState("");
    const fileRef = useRef(null);

    const refresh = async () => {
        try {
            const [wb, cli, proj] = await Promise.all([
                api.get("/project-billing"),
                api.get("/data/clients"),
                api.get("/data/projects"),
            ]);
            setWorkbooks(wb.data.workbooks || []);
            setClients(cli.data.items || []);
            setProjects(proj.data.items || []);
        } catch (e) { /* silent */ }
    };
    useEffect(() => { refresh(); }, []);

    const upload = async (file) => {
        if (!file) return;
        setBusy(true); setPreview(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/project-billing/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const data = r.data;
            setPreview({ ...data, _filename: file.name });
            toast.success(`Parsed ${data.project_count} project(s) · ${data.projects.reduce((s, p) => s + p.blocks.length, 0)} block(s)`);
        } catch (e) {
            toast.error("Preview failed: " + (e.response?.data?.detail || e.message));
        } finally { setBusy(false); }
    };

    const commit = async () => {
        if (!preview) return;
        setCommitting(true);
        try {
            const r = await api.post("/project-billing/commit", {
                filename: preview._filename,
                vendor_llp: preview.vendor_llp,
                projects: preview.projects,
                linked_client_id: linkedClient || null,
                linked_project_id: linkedProject || null,
            });
            toast.success(`Committed ${r.data.workbook.summary.project_count} projects to the vault`);
            setPreview(null);
            setLinkedClient(""); setLinkedProject("");
            if (fileRef.current) fileRef.current.value = "";
            refresh();
        } catch (e) {
            toast.error("Commit failed: " + (e.response?.data?.detail || e.message));
        } finally { setCommitting(false); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this workbook and all its projects? This cannot be undone.")) return;
        try {
            await api.delete(`/project-billing/${id}`);
            toast.success("Workbook deleted");
            refresh();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const totalAgree = workbooks.reduce((s, w) => s + (w.summary?.total_agreement_value || 0), 0);
    const totalRecv = workbooks.reduce((s, w) => s + (w.summary?.total_received || 0), 0);
    const totalBal = workbooks.reduce((s, w) => s + (w.summary?.total_balance || 0), 0);

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">
                    // PROJECT BILLING WORKBOOKS · CONTRACTOR STYLE
                </div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <FolderKanban className="text-[var(--brand-amber)]" size={26} /> Project Billing
                </h1>
                <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                    Multi-project · Multi-block · Scope · Milestones · Receipts · Auto-link to Bank Ledger
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="pb-workbooks" label="Workbooks" value={workbooks.length} format="custom" accent />
                <KpiCard testId="pb-agreement" label="Total Agreement Value" value={fmtINR(totalAgree)} format="custom" />
                <KpiCard testId="pb-received" label="Total Received" value={fmtINR(totalRecv)} format="custom" />
                <KpiCard testId="pb-balance" label="Total Balance" value={fmtINR(totalBal)} format="custom" />
            </div>

            <Panel title="Upload Billing Workbook" subtitle="Upload the .xlsx you (or your contractor) maintain per client — stacked-section format supported">
                <div className="flex items-center gap-3 flex-wrap">
                    <input ref={fileRef} type="file" accept=".xlsx"
                        onChange={(e) => upload(e.target.files[0])}
                        className="hidden" data-testid="pb-file" />
                    <button onClick={() => fileRef.current?.click()} disabled={busy}
                        data-testid="pb-upload"
                        className="px-3 py-2 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-xs tracking-[0.15em] font-semibold flex items-center gap-2 hover:bg-[var(--bg-panel-hover)] disabled:opacity-50">
                        <Upload size={13} /> {busy ? "Parsing…" : "Upload & Preview"}
                    </button>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] italic">
                        Every scope, milestone & receipt is parsed. Nothing is saved until you commit.
                    </div>
                </div>
            </Panel>

            {preview && (
                <PreviewPanel
                    preview={preview}
                    clients={clients} projects={projects}
                    linkedClient={linkedClient} setLinkedClient={setLinkedClient}
                    linkedProject={linkedProject} setLinkedProject={setLinkedProject}
                    onCommit={commit} onCancel={() => setPreview(null)}
                    committing={committing}
                />
            )}

            <Panel title="Saved Workbooks" subtitle={`${workbooks.length} workbook(s) · click any to open the detail view with full drill-down`}>
                {workbooks.length === 0 ? (
                    <div className="text-center py-8 text-[var(--text-secondary)] font-mono-fin text-xs uppercase tracking-wider">
                        No workbooks yet. Upload your first Excel above.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs" data-testid="pb-workbook-table">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2 pr-2">File</th>
                                    <th className="text-left pr-2">Vendor LLP</th>
                                    <th className="text-right pr-2">Projects</th>
                                    <th className="text-right pr-2">Blocks</th>
                                    <th className="text-right pr-2">Receipts</th>
                                    <th className="text-right pr-2">Agreement</th>
                                    <th className="text-right pr-2">Received</th>
                                    <th className="text-right pr-2">Balance</th>
                                    <th className="pr-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {workbooks.map((w) => (
                                    <tr key={w.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]/[0.03]">
                                        <td className="py-1.5 pr-2 text-[var(--text-primary)] truncate max-w-[220px]" title={w.filename}>
                                            <Link data-testid={`pb-open-${w.id}`} to={`/project-billing/${w.id}`} className="text-[var(--brand-amber)] hover:underline flex items-center gap-1">
                                                {w.filename} <ChevronRight size={11} />
                                            </Link>
                                        </td>
                                        <td className="pr-2 text-[var(--text-primary)]/70 max-w-[180px] truncate">{w.vendor_llp || "—"}</td>
                                        <td className="pr-2 text-right text-[var(--text-primary)]/85">{w.summary?.project_count || 0}</td>
                                        <td className="pr-2 text-right text-[var(--text-primary)]/85">{w.summary?.block_count || 0}</td>
                                        <td className="pr-2 text-right text-[var(--text-primary)]/85">{w.summary?.receipt_count || 0}</td>
                                        <td className="pr-2 text-right text-[var(--brand-amber)]">{fmtINR(w.summary?.total_agreement_value || 0)}</td>
                                        <td className="pr-2 text-right text-pos">{fmtINR(w.summary?.total_received || 0)}</td>
                                        <td className="pr-2 text-right text-neg">{fmtINR(w.summary?.total_balance || 0)}</td>
                                        <td className="pr-2 text-right">
                                            <button onClick={() => del(w.id)} data-testid={`pb-del-${w.id}`} className="text-[var(--neg)] hover:text-[var(--text-primary)] p-1" title="Delete">
                                                <Trash2 size={11} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
        </div>
    );
}


function PreviewPanel({ preview, clients, projects, linkedClient, setLinkedClient, linkedProject, setLinkedProject, onCommit, onCancel, committing }) {
    return (
        <Panel
            testId="pb-preview"
            title={`Preview · ${preview.project_count} project(s)`}
            subtitle={`Vendor LLP: ${preview.vendor_llp || "—"} · ${preview.projects.reduce((s, p) => s + p.blocks.length, 0)} block(s) total`}
        >
            <div className="space-y-3">
                {preview.warnings?.length > 0 && (
                    <div className="border border-amber-500/40 bg-amber-500/10 p-3 flex gap-2 items-start">
                        <AlertTriangle size={14} className="text-amber-400 mt-0.5" />
                        <div className="text-xs font-body text-amber-200">
                            {preview.warnings.map((w, i) => <div key={i}>{w}</div>)}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Link to existing Client (optional)</div>
                        <select value={linkedClient} onChange={(e) => setLinkedClient(e.target.value)} data-testid="pb-link-client"
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin text-[var(--text-primary)] focus:border-[var(--brand-amber)] focus:outline-none">
                            <option value="">— no client link —</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.id} · {c.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Link to existing Project (optional)</div>
                        <select value={linkedProject} onChange={(e) => setLinkedProject(e.target.value)} data-testid="pb-link-project"
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin text-[var(--text-primary)] focus:border-[var(--brand-amber)] focus:outline-none">
                            <option value="">— no project link —</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.id} · {p.name}</option>)}
                        </select>
                    </div>
                </div>

                {preview.projects.map((p, pi) => (
                    <div key={pi} className="border border-panel bg-[var(--bg-base)]/40 p-3">
                        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                            <div className="font-display font-bold text-[var(--text-primary)] flex items-center gap-2">
                                <FolderKanban className="text-[var(--brand-amber)]" size={14} />
                                {p.project_name}
                            </div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                Agreement <span className="text-[var(--brand-amber)]">{fmtINR(p.totals?.agreement_value || 0)}</span>
                                · Received <span className="text-pos">{fmtINR(p.totals?.received || 0)}</span>
                                · Balance <span className="text-neg">{fmtINR(p.totals?.balance || 0)}</span>
                            </div>
                        </div>
                        <div className="space-y-2">
                            {p.blocks.map((b, bi) => (
                                <div key={bi} className="border border-panel/60 bg-[var(--bg-base)]/60 px-2 py-1.5 text-[11px] font-mono-fin flex items-center justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <Layers size={11} className="text-[var(--brand-amber)]" />
                                        <span className="text-[var(--text-primary)] truncate max-w-md">Block {bi + 1}: {b.block_name}</span>
                                    </div>
                                    <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">
                                        {b.scopes.length}s / {b.milestones.length}m / {b.receipts.length}r · {fmtINR(b.totals?.balance || 0)} due
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                <div className="flex items-center gap-2 pt-3 border-t border-panel">
                    <button data-testid="pb-commit" onClick={onCommit} disabled={committing}
                        className="px-4 py-2 bg-[var(--pos)] text-black font-mono-fin uppercase text-xs tracking-[0.15em] font-semibold flex items-center gap-2 hover:opacity-90 disabled:opacity-40">
                        <CheckCircle2 size={13} /> {committing ? "Committing…" : `Save ${preview.project_count} Project(s) to Vault`}
                    </button>
                    <button data-testid="pb-cancel" onClick={onCancel} disabled={committing}
                        className="px-4 py-2 border border-panel text-[var(--text-primary)]/70 font-mono-fin uppercase text-xs tracking-[0.15em] hover:border-neg hover:text-neg flex items-center gap-2">
                        Cancel
                    </button>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] italic ml-auto flex items-center gap-1">
                        <Sparkles size={10} /> After commit, add receipts and they auto-post to the bank ledger
                    </div>
                </div>
            </div>
        </Panel>
    );
}
